/**
 * DashboardCard Component
 *
 * iPhone-style card for Dashboard navigation
 * Features:
 * - Clean, modern iOS design
 * - Notification badge support
 * - Smooth hover animations
 * - Flexible icon/title/description layout
 * - Multiple color variants
 */

import React from 'react';
import NotificationBadge from './NotificationBadge';
import styles from './DashboardCard.module.css';

export default function DashboardCard({
  icon,
  title,
  description,
  badge = 0,
  badgeText = '',
  badgeVariant = 'critical',
  badgePulse = false,
  onClick,
  variant = 'primary',
  disabled = false,
  locked = false,
  children
}) {
  // Determine CSS classes
  const cardClasses = [
    styles.card,
    styles[variant],
    disabled && styles.disabled,
    locked && styles.locked
  ].filter(Boolean).join(' ');

  // Handle click
  const handleClick = () => {
    if (!disabled && onClick) {
      onClick();
    }
  };

  // Handle keyboard interaction
  const handleKeyPress = (e) => {
    if ((e.key === 'Enter' || e.key === ' ') && !disabled && onClick) {
      e.preventDefault();
      onClick();
    }
  };

  return (
    <div
      className={cardClasses}
      onClick={handleClick}
      onKeyPress={handleKeyPress}
      role="button"
      tabIndex={disabled ? -1 : 0}
      aria-disabled={disabled}
    >
      {/* Lock icon for onboarding state */}
      {locked && <div className={styles.lockIcon}>🔒</div>}

      {/* Text badge (onboarding: "Empieza aquí", "Requerido") */}
      {badgeText && (
        <div className={`${styles.badgeText} ${badgePulse ? styles.badgeTextPulse : ''}`}>
          {badgeText}
        </div>
      )}

      {/* Notification Badge */}
      {badge > 0 && (
        <NotificationBadge
          count={badge}
          variant={badgeVariant}
          position="top-right"
          pulse={badgePulse}
        />
      )}

      {/* Card Content */}
      <div className={styles.content}>
        {/* Icon */}
        {icon && (
          <div className={styles.iconContainer}>
            {typeof icon === 'string' ? (
              <span className={styles.iconText}>{icon}</span>
            ) : (
              <div className={styles.iconElement}>{icon}</div>
            )}
          </div>
        )}

        {/* Text Content */}
        <div className={styles.textContent}>
          <h3 className={styles.title}>{title}</h3>
          {description && (
            <p className={styles.description}>{description}</p>
          )}
        </div>

        {/* Additional children content */}
        {children && (
          <div className={styles.additionalContent}>
            {children}
          </div>
        )}
      </div>
    </div>
  );
}
